/**
 * Collection portal — fetches related products using CLIP similarity
 * filtered by artist, and renders them as a discovery rail.
 *
 * Primary: Vespa similar-products API with artist filter + CLIP diversity
 * Fallback: Shopify artist collection products.json
 * Last resort: Shopify product recommendations API
 */
class CollectionPortal extends HTMLElement {
  connectedCallback() {
    this.productHandle = this.dataset.productHandle;
    this.productId = this.dataset.productId;
    this.artist = this.dataset.artist;
    this.artistCollectionHandle = this.dataset.artistCollectionHandle;
    this.artistCount = parseInt(this.dataset.artistCount, 10) || 0;
    this.recsUrl = this.dataset.recommendationsUrl;
    this.trackEl = this.querySelector('.portal__track');

    var av = this.querySelector('.portal__av');
    if (av) av.textContent = this.#initials(this.artist);

    let _loaded = false;
    const loadOnce = () => {
      if (_loaded) return;
      _loaded = true;
      // Safety net: if every data source hangs or errors without
      // resolving, hide the portal rather than leaving the skeleton
      // shimmering forever (e.g. an artist with a single artwork —
      // Vespa returns no neighbours and the fallbacks come up empty).
      // Must exceed the worst-case fallback chain: 6.5s (vespa) + 3s
      // (vendor) + 3s (recommendations) = 12.5s.
      this._watchdog = setTimeout(() => {
        if (!this._resolved) this.#hide();
      }, 14000);
      this.#loadFromVespa();
    };

    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver((entries) => {
        if (entries.some(e => e.isIntersecting)) {
          io.disconnect();
          loadOnce();
        }
      }, { rootMargin: '400px' });
      io.observe(this);
    } else {
      loadOnce();
    }
  }

  #initials(name) {
    if (!name) return '';
    var words = name.trim().split(/\s+/);
    // Prefer a numeric token (e.g. "The 13 Prints" → "13")
    var num = words.find(w => /^\d+$/.test(w));
    if (num) return num.slice(0, 2);
    // First letters of meaningful words, skip stop-words
    var stop = new Set(['the', 'a', 'an', 'of', 'and', '&', 'by', 'for']);
    var letters = words.filter(w => !stop.has(w.toLowerCase()))
      .map(w => w[0].toUpperCase());
    return (letters.slice(0, 2).join('')) || (name[0] || '').toUpperCase();
  }

  /**
   * Primary: Vespa CLIP similarity filtered by artist
   */
  async #loadFromVespa() {
    if (!this.artist) return this.#loadFromVendorProducts();

    // Skip the similar-products app-proxy call for flagged bots (set in
    // snippets/posthog.liquid). The Jun-2026 scraper fleet drove ~222k/day
    // HTTP 403s on this endpoint; route them straight to the vendor fallback
    // (Storefront API) so they stop hammering the proxy.
    if (window.__fyBot) return this.#loadFromVendorProducts();

    try {
      var url = '/a/accounts/similar-products?handles=' + encodeURIComponent(this.productHandle)
        + '&artist=' + encodeURIComponent(this.artist)
        + '&full=true&limit=24&diverse=true';

      var controller = new AbortController();
      // 6.5s budget: the diverse-artist query measures 2–5s end-to-end on a COLD
      // cache (Vespa CLIP+grouping cost scales with the artist's catalogue size —
      // catch-all brands match ~19k docs), so the old 3s budget aborted the modal
      // request and logged it as `timeout`. Server-side caching (shop-chat-agent
      // similar-products cache) makes repeats fast; this gives cold queries room.
      var timeout = setTimeout(() => controller.abort(), 6500);
      var resp = await fetch(url, { signal: controller.signal });
      if (!resp.ok) {
        clearTimeout(timeout);
        this.#trackEvent('portal_vespa_error', { error: 'http_' + resp.status });
        return this.#loadFromVendorProducts();
      }
      // Keep the abort timer running through resp.json(): a hung body
      // read would otherwise leave the portal stuck on the skeleton.
      var data = await resp.json();
      clearTimeout(timeout);

      // Hide-ai-art gate (assets/hide-ai-art.js): drop products a gated
      // shopper must not see. window.Fy.isAiHidden exists only for them.
      var aiHidden = window.Fy && window.Fy.isAiHidden;
      var products = ((data && data.products) || [])
        .filter(p => (p.handle || p.id) !== this.productHandle)
        .filter(p => !(aiHidden && aiHidden(p)))
        .slice(0, 20)
        .map(p => this.#mapVespaProduct(p));

      if (products.length === 0) return this.#loadFromVendorProducts();
      this.#renderCards(products, 'vespa_artist_clip');
    } catch (e) {
      this.#trackEvent('portal_vespa_error', { error: e.name === 'AbortError' ? 'timeout' : e.message });
      this.#loadFromVendorProducts();
    }
  }

  /**
   * Fallback: Storefront GraphQL products by vendor.
   *
   * Replaces the old `/collections/artist-<handle>/products.json` fallback:
   * the legacy artist-* collections were deleted from the store when artist
   * pages moved to metaobjects, and Shopify answers products.json for a
   * deleted collection with 200 + an empty list — so the old fallback
   * silently skipped to the (cross-artist) recommendations API instead of
   * showing the artist's work. A vendor query needs no collection to exist.
   */
  async #loadFromVendorProducts() {
    // Gated shoppers: the vendor and recommendations fallbacks carry no AI
    // verdict, so the portal hides instead of showing unchecked art.
    if (window.Fy && window.Fy.hideAiArt) return this.#hide();
    var token = this.#storefrontToken();
    if (!token || !this.artist) return this.#loadFromRecommendations();

    try {
      var controller = new AbortController();
      var timeout = setTimeout(() => controller.abort(), 3000);
      // Storefront search syntax: single quotes inside the vendor name
      // (e.g. O'Keeffe) are escaped with a backslash.
      var vendorQuery = "vendor:'" + this.artist.replace(/\\/g, '\\\\').replace(/'/g, "\\'") + "'";
      var resp = await fetch('/api/2025-07/graphql.json', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': token,
        },
        body: JSON.stringify({
          query: 'query PortalVendorProducts($q: String!) {'
            + ' products(first: 13, query: $q, sortKey: BEST_SELLING) {'
            + ' nodes { handle title featuredImage { url width height } } } }',
          variables: { q: vendorQuery },
        }),
        signal: controller.signal,
      });
      if (!resp.ok) {
        clearTimeout(timeout);
        return this.#loadFromRecommendations();
      }
      var data = await resp.json();
      clearTimeout(timeout);

      var nodes = (data && data.data && data.data.products && data.data.products.nodes) || [];
      var products = nodes
        .filter(p => p.handle !== this.productHandle)
        .slice(0, 12)
        .map(p => {
          var img = p.featuredImage || null;
          return {
            handle: p.handle,
            title: p.title,
            image: img ? img.url : '',
            aspect: (img && img.width && img.height) ? img.width / img.height : 1,
          };
        });

      if (products.length === 0) return this.#loadFromRecommendations();
      this.#renderCards(products, 'vendor_query');
    } catch (e) {
      this.#loadFromRecommendations();
    }
  }

  #storefrontToken() {
    // Same sources media-gallery.js uses for its mock GraphQL fetches.
    var meta = document.querySelector('meta[name="shopify-storefront-api-token"]');
    if (meta && meta.content) return meta.content;
    var el = document.querySelector('[data-storefront-access-token]');
    return (el && el.dataset.storefrontAccessToken) || null;
  }

  /**
   * Last resort: Shopify product recommendations API
   */
  async #loadFromRecommendations() {
    try {
      var url = this.recsUrl + '?product_id=' + this.productId + '&limit=12&intent=related';
      var controller = new AbortController();
      var timeout = setTimeout(() => controller.abort(), 3000);
      var resp = await fetch(url, { headers: { Accept: 'application/json' }, signal: controller.signal });
      if (!resp.ok) {
        clearTimeout(timeout);
        return this.#hide();
      }
      var data = await resp.json();
      clearTimeout(timeout);

      // Recommendations API returns featured_image as a URL string and
      // images as an array of URL strings — different shape from products.json.
      var products = ((data && data.products) || [])
        .filter(p => p.handle !== this.productHandle)
        .slice(0, 20)
        .map(p => {
          var src = '';
          if (typeof p.featured_image === 'string') src = p.featured_image;
          else if (p.featured_image && p.featured_image.src) src = p.featured_image.src;
          else if (p.image && p.image.src) src = p.image.src;
          else if (Array.isArray(p.images) && p.images.length) {
            var first = p.images[0];
            src = typeof first === 'string' ? first : (first && first.src) || '';
          }
          return {
            handle: p.handle,
            title: p.title,
            image: src,
            aspect: 1,
          };
        });

      if (products.length === 0) return this.#hide();

      var byline = this.querySelector('.portal__byline');
      if (byline) byline.innerHTML = this.dataset.tAlsoLike || 'You might also like';
      var all = this.querySelector('.portal__all');
      if (all) all.style.display = 'none';
      var av = this.querySelector('.portal__av');
      if (av) av.style.display = 'none';
      this.#renderCards(products, 'recommendations');
    } catch (e) {
      this.#hide();
    }
  }

  #mapVespaProduct(p) {
    var w = p.image_width || 0;
    var h = p.image_height || 0;
    var aspect = (w && h) ? w / h : 1;
    if (aspect === 1 && p.orientation) {
      var o = p.orientation.toLowerCase();
      if (o === 'portrait') aspect = 0.75;
      else if (o === 'landscape') aspect = 1.33;
    }
    return {
      handle: p.handle || p.id,
      title: p.title || '',
      image: p.image_url || p.imageUrl || '',
      aspect: aspect,
    };
  }

  #renderCards(products, source) {
    this._resolved = true;
    clearTimeout(this._watchdog);

    function thumbUrl(src) {
      if (!src) return '';
      // Cards render at ~78px (clamp 64-78px); _200x covers DPR 2.5. Was _400x
      // (2.6x oversized for the display).
      return src.replace(/\.([a-z]+)(\?|$)/i, '_200x.$1$2');
    }

    var localeMatch = window.location.pathname.match(/^\/([a-z]{2}(?:-[a-z]{2})?)(?:\/|$)/);
    var localePrefix = localeMatch ? '/' + localeMatch[1] : '';

    var html = '<div class="portal__cards">'
      + products.map(p => {
          var imgSrc = thumbUrl(p.image);
          var imgTag = imgSrc
            ? '<img src="' + imgSrc + '" alt="' + (p.title || '').replace(/"/g, '&quot;') + '" loading="lazy">'
            : '';
          return '<a href="' + localePrefix + '/products/' + p.handle + '" class="portal__card" data-portal-product="' + p.handle + '" aria-label="' + (p.title || '').replace(/"/g, '&quot;') + '">'
            + '<div class="portal__img">' + imgTag + '</div>'
            + '</a>';
        }).join('')
      + '</div>';

    this.trackEl.innerHTML = html;

    // Stagger reveal
    var cards = this.trackEl.querySelectorAll('.portal__card');
    for (var i = 0; i < cards.length; i++) {
      (function(card, delay) {
        setTimeout(function() { card.classList.add('portal__card--revealed'); }, delay);
      })(cards[i], 60 * i);
    }

    // Track view when portal enters viewport
    this._viewObserver = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting) {
        this.#trackEvent('portal_collection_viewed', {
          collection_handle: this.artistCollectionHandle || '',
          product_count: this.artistCount || products.length,
          source: source,
        });
        try {
          var v = parseInt(localStorage.getItem('disco_views') || '0', 10);
          localStorage.setItem('disco_views', String(v + 1));
        } catch (e) {}
        this._viewObserver.disconnect();
      }
    }, { threshold: 0.5 });
    this._viewObserver.observe(this);

    this.trackEl.addEventListener('click', e => {
      var card = e.target.closest('[data-portal-product]');
      if (card) {
        this.#trackEvent('portal_product_clicked', {
          source_handle: this.productHandle,
          target_handle: card.dataset.portalProduct,
          collection_handle: this.artistCollectionHandle || '',
          source: source,
        });
      }
    });

    var scrolled = false;
    this.trackEl.addEventListener('scroll', () => {
      if (!scrolled) {
        scrolled = true;
        try { localStorage.setItem('disco_engaged', '1'); } catch (e) {}
      }
    }, { passive: true });

    var allLink = this.querySelector('.portal__all');
    if (allLink) {
      allLink.addEventListener('click', () => {
        this.#trackEvent('portal_view_all_clicked', {
          collection_handle: this.artistCollectionHandle || '',
        });
      });
    }
  }

  #hide() {
    this._resolved = true;
    clearTimeout(this._watchdog);
    this.style.display = 'none';
  }

  #trackEvent(name, props) {
    if (window.posthog) {
      window.posthog.capture(name, Object.assign({}, props, {
        product_handle: this.productHandle,
        pdp_variant: 'new',
      }));
    }
  }
}

if (!customElements.get('collection-portal')) {
  customElements.define('collection-portal', CollectionPortal);
}
